/**
 * Pure view model of the «Шаблоны» tab (contract line #33, T.Z134).
 *
 * Framework-free on purpose: the `unit` Playwright project runs in plain Node,
 * where neither React nor `import.meta.env` exists — nothing here value-imports
 * the api layer (type-only imports are erased at runtime), the same rule the
 * sibling `mailings-model.ts` / `lists-model.ts` document.
 *
 * THE GRAMMAR this file re-implements is the api's own (`apps/api/src/mailing/
 * template-render.ts`): a token is `{{name}}` with optional inner spaces, the
 * name is `[a-zA-Z_][a-zA-Z0-9_]*`, a substituted value is HTML-escaped and an
 * UNKNOWN or UNSET variable is left byte-for-byte. The api scans and renders
 * with exactly this pattern, so what the operator sees in the editor's live
 * chips and preview is what a send will do — the two cannot drift by more than
 * a copy-paste, and the unit specs pin the copy.
 */

import type { EmailTemplateDto, EmailTemplateVariableDto, EmailTemplateVersionMetaDto, MailingChannel } from '../../api/email-templates';
import { formatWhen } from './lists-model';
import { TRACK_URLS } from './mailings-model';

// ------------------------------------------------------------- the channel --

/** The two channels the templates speak, in the picker's order (T.Z210). */
export const CHANNEL_OPTIONS: ReadonlyArray<{ value: MailingChannel; label: string; hint: string }> = [
  { value: 'EMAIL', label: 'Email', hint: 'Письмо с HTML-вёрсткой уходит через почтовый сервис.' },
  { value: 'SMS', label: 'SMS', hint: 'Короткое текстовое сообщение — без HTML и темы; подпись отправителя задаёт окружение.' },
];

/** The channel a stored template speaks — an api older than v1.9.72 means EMAIL. */
export function channelOf(template: Pick<EmailTemplateDto, 'channel'>): MailingChannel {
  return template.channel ?? 'EMAIL';
}

export function channelLabel(channel: MailingChannel): string {
  return channel === 'SMS' ? 'SMS' : 'Email';
}

/** True when the template's body is PLAIN TEXT (the editor drops the HTML mode). */
export function isPlainTextChannel(channel: MailingChannel): boolean {
  return channel === 'SMS';
}

/** The token grammar — ONE regex for the scan and the render, so they agree. */
export const TEMPLATE_VARIABLE_PATTERN = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

/** The api's own escape (a ФИО never closes a tag early). */
export function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * The variable inventory of a draft: every distinct token name, subject first,
 * then the body, in first-appearance order — exactly what the api stores in
 * `email_template_versions.variables`, so the chips the editor shows are the
 * inventory the save will record.
 */
export function scanTemplateVariables(subject: string, bodyHtml: string): string[] {
  const found: string[] = [];
  for (const content of [subject, bodyHtml]) {
    for (const match of content.matchAll(TEMPLATE_VARIABLE_PATTERN)) {
      const name = match[1];
      if (name !== undefined && !found.includes(name)) found.push(name);
    }
  }
  return found;
}

/** What a caller may pass for a variable. `null`/`undefined` = unset (left as-is). */
export type TemplateVariableValue = string | number | null | undefined;

/**
 * Substitutes `{{var}}` tokens. Unknown and unset variables stay verbatim —
 * the recipient would see the token, so the operator must see it too.
 */
export function substituteTemplateTokens(content: string, vars: Record<string, TemplateVariableValue>): string {
  return content.replace(TEMPLATE_VARIABLE_PATTERN, (whole: string, name: string) => {
    const value = vars[name];
    if (value === undefined || value === null) return whole;
    return escapeHtml(String(value));
  });
}

// ------------------------------------------------------------- the preview --

/** The sample «Имя Отчество» the preview substitutes — a plausible patronymic, never a real person. */
export const SAMPLE_GREETING_NAME = 'Виктория Андреевна';

/** The two tracks a preview can fold — the same welcome pages a real send uses. */
export type PreviewTrack = 'AI' | 'PYTHON';

/** The sample variable map of ONE preview: the greeting name and the track's welcome URL. */
export function sampleVariables(track: PreviewTrack): Record<string, string> {
  return {
    name: SAMPLE_GREETING_NAME,
    start_url: TRACK_URLS[track],
    gosuslugi_test_link: SAMPLE_GOSUSLUGI_URL,
    // T.Z211 — the short links preview as a shape-real URL that owns NO code:
    // a preview has no send run behind it, so no code may be minted (a working
    // per-child link must never reach an operator's screen). Same reasoning as
    // the api's #34.7 sample — the TEST send #34.8 mints a real childless code
    // instead (T.Z212), which is why this client-side sample stays preview-only.
    short_start_url: SAMPLE_SHORT_URL,
    short_gosuslugi_test_link: SAMPLE_SHORT_URL,
  };
}

/** Both halves of a preview: the substituted subject and the substituted body. */
export function renderPreview(
  subject: string,
  bodyHtml: string,
  track: PreviewTrack,
): { subject: string; html: string } {
  const vars = sampleVariables(track);
  return {
    subject: substituteTemplateTokens(subject, vars),
    html: substituteTemplateTokens(bodyHtml, vars),
  };
}

/**
 * The human line a variable chip carries — the two convention names get real
 * words. T.Z184: the `start_url` hint says HOW the value appears (the child's
 * programme picks the welcome page — the operator composes nothing), the same
 * fact `TRACK_START_URL` implements on the api side.
 */
export const VARIABLE_HINTS: Readonly<Record<string, string>> = {
  name: 'Имя Отчество получателя',
  start_url: 'Ссылка на страницу курса — ИИ или Python, разрешается автоматически по программе ребёнка',
  // T.Z185 (seed v2) — the consent-error letter's second link; T.Z201 — the SAME
  // variable carries the course page of the «Пройдите вступительное испытание»
  // letter; T.Z206 — a mailing LIST fills it too when its pinned template uses
  // it (a recipient whose link does not resolve is skipped, never mailed with a
  // raw token). The hint says so honestly, because in letters that neither name
  // the variable nor fill it the token would go out verbatim.
  gosuslugi_test_link:
    'Страница курса ребёнка на Госуслугах — заполняется в письмах «Ошибка подписи договора: не указано согласие родителей», «Пройдите вступительное испытание» и в рассылках списками, чей шаблон использует переменную; получатель, для которого ссылка не определяется, не отправляется. В остальных письмах уйдёт как есть',
  // T.Z211 — the per-recipient SHORT links. Same targets as the two above, but
  // the letter carries `https://c.ismart.org/<код>` — FOUR characters, minted
  // per recipient per variable per SEND RUN, and every click is counted for
  // exactly that child (the panel shows «перешли по ссылке», the recipients
  // table shows «кликнул» + when). Channel-agnostic: made for SMS (no room for
  // a long URL, no tracking pixel), free to use in e-mail. The preview and the
  // test letter substitute a shape-real SAMPLE that owns no code.
  short_start_url:
    'Короткая ссылка на страницу курса ребёнка (https://c.ismart.org/XXXX, 4 символа) — своя для каждого получателя в каждой рассылке, переходы считаются по конкретному ребёнку. Канал не важен: рассчитана на SMS, годится и для писем',
  short_gosuslugi_test_link:
    'Короткая ссылка на страницу курса ребёнка на Госуслугах (https://c.ismart.org/XXXX, 4 символа) — своя для каждого получателя в каждой рассылке, переходы считаются по конкретному ребёнку. Получатель, для которого ссылка не определяется, не отправляется',
};

/** The Госуслуги stand-in the preview substitutes — a REAL-shape futurecode URL
 * (T.Z207): the course number, the `view=` and the `aud=` segment in the exact
 * form a real letter carries, so a preview never teaches the operator a link
 * shape the api stopped rendering. «ИИ 10-11» офлайн = course 31608. */
export const SAMPLE_GOSUSLUGI_URL = 'https://www.gosuslugi.ru/futurecode/31608?view=offline&aud=10_11';

/** The short-link stand-in a preview substitutes (T.Z211) — the EXACT shape a
 * real letter carries (`<host>/<4 base62 chars>`), never a minted code. `demo`
 * is a legal base62 code the api allocates with probability ~1 in 15 million,
 * so a preview screenshot teaches the shape without pointing at a child. */
export const SAMPLE_SHORT_URL = 'https://c.ismart.org/demo';

export function describeVariable(name: string): string {
  return VARIABLE_HINTS[name] ?? `переменная «${name}» — значение задаётся при отправке`;
}

/** True when the chip's name is one the send pipeline knows how to fill. */
export function isKnownVariable(name: string): boolean {
  return name in VARIABLE_HINTS;
}

// ------------------------------------------------------------ the validates --

/** The controller's own field limits — the UI refuses before the round trip. */
export const TEMPLATE_LIMITS = {
  KEY_MAX: 64,
  TITLE_MAX: 200,
  SUBJECT_MAX: 500,
  BODY_MAX: 200_000,
  NOTE_MAX: 1_000,
} as const;

/** The key grammar: a latin slug (a-z, 0-9, dashes), 2–64 chars — the api's 400 phrased in RU. */
export const TEMPLATE_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{1,63}$/;

export function validateTemplateKey(key: string): string | null {
  const trimmed = key.trim();
  if (trimmed.length === 0) return 'Укажите ключ шаблона.';
  if (!TEMPLATE_KEY_PATTERN.test(trimmed)) {
    return 'Ключ — латиница, цифры и дефисы, 2–64 знака, начинается с буквы или цифры (например documents-unsigned).';
  }
  return null;
}

/** One editable draft — the same shape for «новый шаблон» and «новая версия». */
export interface TemplateDraft {
  key: string;
  title: string;
  /** T.Z210 — the draft's channel (a NEW template only; a version inherits). */
  channel: MailingChannel;
  subject: string;
  bodyHtml: string;
  changelogNote: string;
}

export const EMPTY_DRAFT: TemplateDraft = { key: '', title: '', channel: 'EMAIL', subject: '', bodyHtml: '', changelogNote: '' };

type DraftRefusal = { ok: false; error: string };
type DraftBuild<T> = { ok: true; value: T } | DraftRefusal;

/** The trimmed wire body of #33.2 (key + title + v1) — or the RU refusal. The
 * channel travels ONLY when it is SMS: absent = EMAIL is the contract's own
 * default (ADDITIVE v1.9.72), so an e-mail draft keeps the pre-T.Z210 body
 * byte for byte. */
export function buildTemplateCreate(draft: TemplateDraft): DraftBuild<{
  key: string;
  title: string;
  channel?: MailingChannel;
  subject: string;
  bodyHtml: string;
  changelogNote?: string;
}> {
  const key = (draft.key ?? '').trim();
  const keyError = validateTemplateKey(key);
  if (keyError) return { ok: false, error: keyError };
  const title = (draft.title ?? '').trim();
  if (title.length === 0) return { ok: false, error: 'Укажите название шаблона — его видят в списке и в выборе шаблона.' };
  if (title.length > TEMPLATE_LIMITS.TITLE_MAX) return { ok: false, error: `Название — не больше ${TEMPLATE_LIMITS.TITLE_MAX} знаков.` };
  const body = buildVersionBody(draft);
  if (!body.ok) return body;
  return { ok: true, value: { key, title, ...(draft.channel === 'SMS' ? { channel: 'SMS' as const } : {}), ...body.value } };
}

/** The trimmed wire body of #33.4 — a NEW version (no key, no title). */
export function buildVersionCreate(draft: TemplateDraft): DraftBuild<{
  subject: string;
  bodyHtml: string;
  changelogNote?: string;
}> {
  return buildVersionBody(draft);
}

/** The shared half: subject + body are mandatory, the note is optional and capped. */
function buildVersionBody(draft: TemplateDraft): DraftBuild<{
  subject: string;
  bodyHtml: string;
  changelogNote?: string;
}> {
  // Every field is read through `?? ''` — a caller may hand a partial draft
  // (the create fold does not keep key/title, the version fold does not need
  // them), and a missing field is a REFUSAL, never a thrown TypeError.
  const subject = (draft.subject ?? '').trim();
  if (subject.length === 0) return { ok: false, error: 'Укажите тему письма.' };
  if (subject.length > TEMPLATE_LIMITS.SUBJECT_MAX) {
    return { ok: false, error: `Тема — не больше ${TEMPLATE_LIMITS.SUBJECT_MAX} знаков.` };
  }
  const bodyHtml = draft.bodyHtml ?? '';
  if (bodyHtml.trim().length === 0) {
    return { ok: false, error: draft.channel === 'SMS' ? 'Введите текст сообщения.' : 'Вставьте HTML письма.' };
  }
  if (bodyHtml.length > TEMPLATE_LIMITS.BODY_MAX) {
    return { ok: false, error: `Текст письма — не больше ${TEMPLATE_LIMITS.BODY_MAX} знаков.` };
  }
  const changelogNote = (draft.changelogNote ?? '').trim();
  if (changelogNote.length > TEMPLATE_LIMITS.NOTE_MAX) {
    return { ok: false, error: `Пометка о версии — не больше ${TEMPLATE_LIMITS.NOTE_MAX} знаков.` };
  }
  return {
    ok: true,
    value: { subject, bodyHtml, ...(changelogNote.length > 0 ? { changelogNote } : {}) },
  };
}

// ---------------------------------------------------------------- ordering --

/**
 * #33.1's own order, restated client-side so a fresh answer AND a stale cache
 * read the same: live templates first (a mutation that flips the flag must not
 * leave the row in place until a refetch wins the race), key order inside each
 * group. Pure — the input array is not touched.
 */
export function sortTemplates(templates: EmailTemplateDto[]): EmailTemplateDto[] {
  return [...templates].sort((a, b) => {
    const aArchived = a.archivedAt !== null ? 1 : 0;
    const bArchived = b.archivedAt !== null ? 1 : 0;
    if (aArchived !== bArchived) return aArchived - bArchived;
    return a.key.localeCompare(b.key);
  });
}

export function isArchived(template: Pick<EmailTemplateDto, 'archivedAt'>): boolean {
  return template.archivedAt !== null;
}

export function templateStatusLabel(template: Pick<EmailTemplateDto, 'archivedAt'>): { label: string; tone: 'ok' | 'neutral' } {
  return isArchived(template) ? { label: 'В архиве', tone: 'neutral' } : { label: 'Активен', tone: 'ok' };
}

/** The newest version row (the list keeps them oldest first) — or null. */
export function latestVersionOf(template: EmailTemplateDto): EmailTemplateVersionMetaDto | null {
  return template.versions[template.versions.length - 1] ?? null;
}

/** One cell line of the versions list: number, when, the note if there is one. */
export function formatVersionLine(version: EmailTemplateVersionMetaDto): string {
  const note = version.changelogNote ? ` — ${version.changelogNote}` : '';
  return `версия ${version.version} · ${formatWhen(version.createdAt)}${note}`;
}

/** The summary under the title: «3 версии · последняя 18.09 12:00». */
export function formatVersionsSummary(template: EmailTemplateDto): string {
  const count = template.versions.length;
  if (count === 0) return 'нет версий';
  const latest = latestVersionOf(template);
  const plural = count === 1 ? 'версия' : count < 5 ? 'версии' : 'версий';
  return `${count} ${plural} · последняя ${latest ? formatWhen(latest.createdAt) : '—'}`;
}

// ------------------------------------------------------------- the errors --

/** The codes the page translates — anything else falls through to the api's own words. */
export type TemplateErrorShape = { code?: string; message?: string };

/** The line when there is nothing at all to explain — still a sentence, not a blank. */
export const UNSAVED_FALLBACK = 'Не удалось сохранить. Попробуйте ещё раз.';

/**
 * The RU line for a refused write. The codes are the api's own (#33's error
 * table); the honest rule is the api's message when the code is unknown — the
 * operator should never see a bare «HTTP 409» with no explanation.
 */
export function templateErrorLine(error: TemplateErrorShape | null | undefined): string {
  if (!error) return '';
  switch (error.code) {
    case 'TEMPLATE_KEY_EXISTS':
      return 'Шаблон с таким ключом уже существует — выберите другой ключ (существующий не изменить).';
    case 'TEMPLATE_VERSION_EXISTS':
      return 'Такую версию только что создали в другом окне — обновите список и проверьте, не совпадает ли правка.';
    case 'TEMPLATE_ARCHIVED':
      return 'Шаблон в архиве — новые версии для него запрещены. Сначала верните его из архива.';
    case 'TEMPLATE_NOT_FOUND':
      return 'Шаблон не найден — возможно, его переименовали или удалили в другом окне. Обновите список.';
    default:
      return error.message ?? 'Не удалось сохранить. Попробуйте ещё раз.';
  }
}

/**
 * The `unknown` a react-query mutation hands over, folded onto
 * {@link templateErrorLine}. The check is STRUCTURAL on purpose — importing the
 * `ApiError` class as a value would drag the transport into a plain-Node module.
 */
export function errorLineOf(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const shape = error as TemplateErrorShape;
    if (typeof shape.code === 'string') return templateErrorLine(shape);
  }
  if (error instanceof Error && error.message) return error.message;
  return UNSAVED_FALLBACK;
}

/** The chips a view shows: the documented inventory, description or the fallback words. */
export function variableChips(variables: EmailTemplateVariableDto[] | undefined): Array<{ name: string; text: string; known: boolean }> {
  return (variables ?? []).map((variable) => ({
    name: variable.name,
    text: variable.description ? `${variable.name} — ${variable.description}` : `${variable.name} — ${describeVariable(variable.name)}`,
    known: isKnownVariable(variable.name),
  }));
}
